import React, { useEffect, useState } from 'react';
import { useIntl } from 'react-intl';

import { useToast } from '~/eds/src/components/hooks/useToast';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { LoadingShimmer } from '~/eds/src/components/LoadingShimmer/LoadingShimmer';
import { Text } from '~/eds/src/components/Text/Text';
import { useFormatNumber } from '~/i18n/hooks/useFormatNumber/useFormatNumber';
import { api } from '~/redux/api/createApi';

import { SearchQuery } from '../types';
import { isActiveQuery } from './QueryFilters.utils';

interface Props {
  query: SearchQuery;
  onDocsInScopeFound?: (total: number) => void;
}

export const QueryScope = ({ query, onDocsInScopeFound }: Props) => {
  const { toast } = useToast();
  const intl = useIntl();
  const formatNumber = useFormatNumber();
  const queryWithValues = isActiveQuery(query);

  const {
    data: docsInScope = {
      meta: {
        total: 0,
      },
    },
    isLoading: isLoadingDocsInScope,
    isFetching: isFetchingDocsInScope,
    error: errorDocsInScope,
  } = api.endpoints.searchV3Documents.useQuery(
    {
      page: 1,
      pageSize: 20,
      query: queryWithValues,
      store_as_recent_search: false,
    },
    {
      skip: queryWithValues.length <= 0,
    },
  );

  const [count, setCount] = useState(0);

  const isLoading = isFetchingDocsInScope || isLoadingDocsInScope;

  if (errorDocsInScope) {
    toast({
      status: 'danger',
      message: intl.formatMessage({
        id: 'QueryScope.failedToGetDocuments',
        defaultMessage: 'Failed to get documents in scope',
        description: 'Error message when getting documents in scope fails',
      }),
    });
  }

  useEffect(() => {
    if (!docsInScope || queryWithValues.length <= 0) {
      setCount(0);
    } else {
      setCount(docsInScope.meta.total);
    }
    if (onDocsInScopeFound) {
      onDocsInScopeFound(count);
    }
  }, [queryWithValues, docsInScope]);

  return (
    <Layout preset="xs">
      <Text>
        {intl.formatMessage({
          id: 'QueryScope.documentsFoundLabel',
          defaultMessage: 'Documents Found: ',
          description: 'Label for documents found count',
        })}
      </Text>
      {isLoading ? (
        <LoadingShimmer {...LOAD_DOCUMENT_COUNT} />
      ) : (
        <Text color="status.active">{formatNumber(count)}</Text>
      )}
    </Layout>
  );
};

const LOAD_DOCUMENT_COUNT = { width: '50', height: 'filter.height' };
